/**
 * "Jev decisions": what Jev decided on this document in its latest jevassist
 * run. Fed by GET /api/aiAnnotation/getJevDecisions (okf-be
 * ai/jev/decisionsView.js). Dev/staging only — opened from the floating
 * button next to View settings.
 *
 *   Decisions  sent back · dropped · kept with a flag · all kept, each row
 *              with the full quote, the observation, speaker, signal with
 *              Jev's probability and confidence, reasons and the seven check
 *              scores (the ones past a threshold in red)
 *   Speakers   side, role, usage with Jev's confidences
 *   Fields     nature per field, triage per chunk
 *   Run        C1 calls, token usage, the live thresholds
 *
 * Styled like every other modal in the app: oolib typography (typoV2_CSS
 * mixins / UI_* components) and the oolib palette (colors2). No page-theme
 * tokens — the Modal renders in a portal outside the V3 theme root.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Modal, TabBarV2 as TabBarV2Raw, colors2, typoV2_CSS } from "oolib";

const TabBarV2 = TabBarV2Raw as React.FC<any>;

// ─── data shapes (mirror decisionsView.js) ──────────────────────────────────
type Scores = Partial<Record<"insight" | "none_fits" | "misread" | "invented" | "generic_only" | "multi_thought" | "incomplete", number>>;
type Row = {
  id: string; valuePath: string; field: string; chunkIdx: number | null; text: string; actorName: string | null;
  span: { start: number; end: number; stage: string } | null;
  observation: { title: string | null; desc: string | null } | null;
  outcome: "keep" | "drop" | "sendBack"; reasons: string[]; flags: string[];
  signal: string | null; signalConfidence: number | null; signalProbability: number | null; category: string | null; categoryConfidence: number | null;
  runnerUp: { key: string; p: number } | null; scores: Scores;
  persona: { side: string; role: string | null; roleConfidence: number | null; usage: string | null; usageCue: number | null } | null;
};
type Speaker = { name: string; turns: number | null; side: string; productSideP: number | null; role: string | null; roleConfidence: number | null; relationship: string | null; usage: string | null; usageCue: number | null };
type Field = { valuePath: string; label: string; text: string | null; nature: string | null; confidence: number | null; hasSpeakerLabels: number | null; units: { chunkIdx: number | null; chars: number | null; skipped: boolean; reasons: string[]; scores: Record<string, number> }[] };
export type JevDecisionsData = {
  available: boolean; reason?: string; mode?: string;
  run?: { id: string; at: string; model: string | null; c1Model: string | null };
  totals?: { highlights: number; kept: number; dropped: number; sentBack: number; flagged: number; overridden: number };
  fields?: Field[]; speakers?: Speaker[]; singleVoice?: any;
  decisions?: { sentBack: Row[]; dropped: Row[]; flagged: Row[]; kept: Row[] };
  c1?: { field: string; chunkIdx: number | null; nature: string | null; highlights: number | null; ms: number | null }[];
  usage?: {
    jev: { input: number; output: number; requests: number; costUsd: number };
    c1: { input: number; output: number; cacheRead: number; cacheWrite: number; costUsd: number };
    totalUsd: number;
    rates: { jev: { input: number; output: number }; claude: { model: string | null; input: number; cacheRead: number; cacheWrite: number; output: number } };
  };
  thresholds?: any;
};

// ─── formatting ─────────────────────────────────────────────────────────────
const pct = (x: number | null | undefined) => (x == null ? "–" : `${Math.round(x * 100)}%`);
const num = (x: number | null | undefined) => (x == null ? "–" : x.toLocaleString());
const usd = (x: number | null | undefined) => (x == null ? "–" : x < 0.01 ? `$${x.toFixed(4)}` : `$${x.toFixed(2)}`);
const words = (s: string | null | undefined) => String(s || "").replace(/_/g, " ");

// The seven moderator checks, in the order the thresholds page lists them.
// "stands alone" is Jev's `incomplete` check: does the quote state its own
// point without the surrounding chunk (a dangling "that", a half sentence).
const CHECKS: { key: keyof Scores; label: string; bad: (v: number, th: any) => boolean }[] = [
  { key: "insight", label: "worth keeping", bad: (v, th) => v < (th?.drop?.insightBelow ?? 0.4) },
  { key: "none_fits", label: "no signal fits", bad: (v, th) => v >= (th?.drop?.noneFitsAtLeast ?? 0.6) },
  { key: "generic_only", label: "bare verdict", bad: (v, th) => v >= (th?.drop?.genericAtLeast ?? 0.8) },
  { key: "misread", label: "misread", bad: (v, th) => v >= (th?.sendBack?.misreadAtLeast ?? 0.5) },
  { key: "invented", label: "invented", bad: (v, th) => v >= (th?.sendBack?.inventedAtLeast ?? 0.5) },
  { key: "multi_thought", label: "two points", bad: (v, th) => v >= (th?.flag?.multiThoughtAtLeast ?? th?.sendBack?.multiThoughtAtLeast ?? 0.6) },
  { key: "incomplete", label: "needs context", bad: (v, th) => v >= (th?.sendBack?.incompleteAtLeast ?? 0.6) },
];

// ─── styles ─────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  ${typoV2_CSS.UI_BODY_SM_DF};
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  height: calc(100dvh - 11rem);
  min-height: 420px;
  color: ${colors2.black};
`;
const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding: 10px 12px;
  border: 1px solid ${colors2.grey10};
  border-radius: 10px;
  background: ${colors2.grey2};
  color: ${colors2.grey80};
`;
const Big = styled.span`
  ${typoV2_CSS.UI_BODY_BOLD_DF};
  color: ${colors2.black};
`;
const Muted = styled.span`
  color: ${colors2.grey60};
`;
const Small = styled.div`
  ${typoV2_CSS.UI_CAPTION_DF};
  color: ${colors2.grey60};
`;
const Pill = styled.span<{ $fg?: string; $bg?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $fg }) => $fg || "#3A4A63"};
  background: ${({ $bg }) => $bg || "#EEF1F5"};
`;
const RED = { fg: "#9F1D1D", bg: "#FBE9E9" };
const AMBER = { fg: "#7A4B00", bg: "#FFF1CF" };
const GREEN = { fg: "#1F6F43", bg: "#E3F4EA" };
const BLUE = { fg: "#126DED", bg: "#EAF2FE" };
const PURPLE = { fg: "#7A12B8", bg: "#F4E9FC" };

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
const Panel = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-top: 8px;
`;
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const SectionHead = styled.button`
  all: unset;
  ${typoV2_CSS.UI_BODY_BOLD_SM_DF};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-weight: 700;
  color: ${colors2.black};
  border-bottom: 1px solid ${colors2.grey10};
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th {
    ${typoV2_CSS.UI_CAPTION_DF};
    text-align: left;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors2.grey60};
    padding: 6px 8px 4px;
    white-space: nowrap;
  }
  td {
    ${typoV2_CSS.UI_BODY_SM_DF};
    padding: 8px;
    border-top: 1px solid ${colors2.grey10};
    vertical-align: top;
    color: ${colors2.grey80};
  }
  td.num { font-variant-numeric: tabular-nums; white-space: nowrap; }
`;
const Quote = styled.div`
  color: ${colors2.black};
  line-height: 1.45;
  white-space: pre-wrap;
  cursor: pointer;
  &:hover { background: ${colors2.grey5}; }
`;
const ContextText = styled.div`
  ${typoV2_CSS.UI_BODY_SM_DF};
  white-space: pre-wrap;
  line-height: 1.6;
  color: ${colors2.grey80};
  max-height: calc(100dvh - 14rem);
  overflow: auto;
  padding: 16px;
  mark {
    background: #FFE58A;
    color: ${colors2.black};
    padding: 1px 0;
  }
`;
const Scorebar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;
const Empty = styled.div`
  padding: 24px 12px;
  color: ${colors2.grey60};
`;

// ─── pieces ─────────────────────────────────────────────────────────────────
const ThresholdLine: React.FC<{ th: any }> = ({ th }) => (
  <Small>
    Drop when worth keeping &lt; {pct(th?.drop?.insightBelow)}, no signal fits ≥ {pct(th?.drop?.noneFitsAtLeast)}, or bare verdict ≥ {pct(th?.drop?.genericAtLeast)}.
    {" "}Send back when misread ≥ {pct(th?.sendBack?.misreadAtLeast)}, invented ≥ {pct(th?.sendBack?.inventedAtLeast)}, or needs context ≥ {pct(th?.sendBack?.incompleteAtLeast)}.
    {" "}Flag when worth keeping &lt; {pct(th?.flag?.insightBorderlineBelow)}, signal confidence &lt; {pct(th?.flag?.signalUncertainBelow)}, or two points ≥ {pct(th?.flag?.multiThoughtAtLeast ?? th?.sendBack?.multiThoughtAtLeast)}.
  </Small>
);

const ScoreChips: React.FC<{ scores: Scores; thresholds: any }> = ({ scores, thresholds }) => (
  <Scorebar>
    {CHECKS.map(({ key, label, bad }) => {
      const v = scores?.[key];
      if (typeof v !== "number") return null;
      const isBad = bad(v, thresholds);
      return (
        <Pill key={key} $fg={isBad ? RED.fg : undefined} $bg={isBad ? RED.bg : undefined} title={`${label}: ${pct(v)}`}>
          {label} {pct(v)}
        </Pill>
      );
    })}
  </Scorebar>
);

// Signal: Jev's probability for the pick (what it was chosen on), its separate
// calibrated confidence, the category, and the runner-up's probability.
const SignalCell: React.FC<{ r: Row }> = ({ r }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <span>
      <Pill $fg={BLUE.fg} $bg={BLUE.bg}>{r.signal || "no signal"}</Pill> C: {pct(r.signalConfidence)} · P: {pct(r.signalProbability)}
    </span>
    <Small>
      {r.runnerUp ? `next ${words(r.runnerUp.key)} P: ${pct(r.runnerUp.p)}` : ""}
      {r.category ? `${r.runnerUp ? " · " : ""}${r.category} C: ${pct(r.categoryConfidence)}` : ""}
    </Small>
  </div>
);

const Rows: React.FC<{ rows: Row[]; thresholds: any; showReasons: boolean; onContext: (r: Row) => void }> = ({ rows, thresholds, showReasons, onContext }) => {
  if (!rows.length) return <Empty>Nothing here.</Empty>;
  return (
    <div style={{ overflowX: "auto" }}>
      <Table>
        <thead>
          <tr>
            <th style={{ width: "30%" }}>Highlight</th>
            <th style={{ width: "22%" }}>Observation</th>
            <th>Speaker</th>
            <th>Signal</th>
            {showReasons && <th>Why</th>}
            <th>Checks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.valuePath}:${r.chunkIdx}:${r.id}`}>
              <td>
                <Quote title="Show in the field's text" onClick={() => onContext(r)}>{r.text || "(no quote recorded)"}</Quote>
                <Small>
                  {r.field}
                  {r.chunkIdx != null ? ` · part ${r.chunkIdx + 1}` : ""}
                </Small>
              </td>
              <td>
                {r.observation ? (
                  <>
                    <div style={{ fontWeight: 600, color: colors2.black }}>{r.observation.title}</div>
                    <div>{r.observation.desc}</div>
                  </>
                ) : (
                  <Muted>not recorded for this run</Muted>
                )}
              </td>
              <td>
                <div>{r.actorName || <Muted>–</Muted>}</div>
                {r.persona && (
                  <Small>
                    {r.persona.side === "product" ? "product side" : [r.persona.role, r.persona.usage && r.persona.usage !== "Unknown" ? r.persona.usage : null].filter(Boolean).join(" · ") || "customer"}
                  </Small>
                )}
              </td>
              <td><SignalCell r={r} /></td>
              {showReasons && (
                <td>
                  <Scorebar>
                    {r.reasons.map((x) => <Pill key={x} $fg={r.outcome === "drop" ? RED.fg : AMBER.fg} $bg={r.outcome === "drop" ? RED.bg : AMBER.bg}>{x}</Pill>)}
                    {r.flags.map((x) => <Pill key={x} $fg={AMBER.fg} $bg={AMBER.bg}>{x}</Pill>)}
                  </Scorebar>
                </td>
              )}
              <td><ScoreChips scores={r.scores} thresholds={thresholds} /></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const Group: React.FC<{ title: string; count: number; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Section>
      <SectionHead type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span style={{ width: 12, display: "inline-block" }}>{open ? "▾" : "▸"}</span>
        {title} <Muted>({count})</Muted>
      </SectionHead>
      {open && children}
    </Section>
  );
};

const sideColors = (side: string) => (side === "product" ? PURPLE : side === "unclear" ? AMBER : GREEN);

// ─── quote in context ───────────────────────────────────────────────────────
// A second modal on top: the whole field text with the quote marked and
// scrolled into view. The span comes from the endpoint (shared matcher);
// without one, a plain search is tried and otherwise the text shows unmarked.
const QuoteContextModal: React.FC<{ row: Row; field?: Field; onClose: () => void }> = ({ row, field, onClose }) => {
  const markRef = useRef<HTMLElement | null>(null);
  const text = field?.text || "";
  const span = useMemo(() => {
    if (row.span && row.span.end <= text.length) return row.span;
    const i = text.indexOf(row.text);
    return i >= 0 ? { start: i, end: i + row.text.length, stage: "client" } : null;
  }, [row, text]);
  useEffect(() => { markRef.current?.scrollIntoView({ block: "center" }); }, [span]);
  return (
    <Modal title={`${row.field}${row.chunkIdx != null ? ` · part ${row.chunkIdx + 1}` : ""} — ${row.actorName || "quote"} in context`} desktopWidth="70%" onClose={onClose}>
      {!text && <Empty>The field text is not in this payload.</Empty>}
      {!!text && (
        <ContextText>
          {span ? (
            <>
              {text.slice(0, span.start)}
              <mark ref={markRef as any}>{text.slice(span.start, span.end)}</mark>
              {text.slice(span.end)}
            </>
          ) : (
            <>
              <Small style={{ marginBottom: 8 }}>The quote could not be located in this text; showing the whole field.</Small>
              {text}
            </>
          )}
        </ContextText>
      )}
    </Modal>
  );
};

// ─── modal ──────────────────────────────────────────────────────────────────
type Tab = "decisions" | "speakers" | "fields" | "run";

type Props = { data?: JevDecisionsData; loading: boolean; onClose: () => void };

export const JevDecisionsModal: React.FC<Props> = ({ data, loading, onClose }) => {
  const [tab, setTab] = useState<Tab>("decisions");
  const [contextRow, setContextRow] = useState<Row | null>(null);
  const th = data?.thresholds;
  const fieldByPath = useMemo(() => new Map((data?.fields || []).map((f) => [f.valuePath, f])), [data]);
  const keptPlain = useMemo(() => (data?.decisions?.kept || []).filter((r) => !r.flags.length), [data]);

  return (
    <Modal title="Jev decisions" desktopWidth="90%" onClose={onClose}>
        <Wrap>
          {loading && <Empty>Reading the run…</Empty>}
          {!loading && data && !data.available && <Empty>No Jev run recorded for this document yet. {data.reason ? `(${data.reason})` : ""}</Empty>}
          {!loading && data?.available && (
            <>
              <Strip>
                <Big>{num(data.totals?.highlights)}</Big> <Muted>highlights →</Muted>
                <Pill $fg={GREEN.fg} $bg={GREEN.bg}>kept {num(data.totals?.kept)}</Pill>
                <Pill $fg={RED.fg} $bg={RED.bg}>dropped {num(data.totals?.dropped)}</Pill>
                <Pill $fg={AMBER.fg} $bg={AMBER.bg}>sent back {num(data.totals?.sentBack)}</Pill>
                <Pill $fg={AMBER.fg} $bg={AMBER.bg}>flagged {num(data.totals?.flagged)}</Pill>
                {data.totals?.overridden ? <Pill>{num(data.totals.overridden)} edited by a person since</Pill> : null}
                <Muted style={{ marginLeft: "auto" }}>
                  {data.run?.at ? new Date(data.run.at).toLocaleString() : ""} · {data.run?.model || "jev"} + {data.run?.c1Model || "C1"}
                </Muted>
              </Strip>
              <ThresholdLine th={th} />

              <Body>
                <TabBarV2
                  options={[
                    { display: "Decisions", value: "decisions" },
                    { display: `Speakers (${data.speakers?.length || 0})`, value: "speakers" },
                    { display: `Fields (${data.fields?.length || 0})`, value: "fields" },
                    { display: "Run", value: "run" },
                  ]}
                  saveValueAsString
                  value={tab}
                  onChange={(_k: string, v: string) => setTab(v as Tab)}
                />
                <Panel>
                  {tab === "decisions" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
                      <Group title="Sent back — logged and left out this phase" count={data.decisions?.sentBack.length || 0}>
                        <Rows rows={data.decisions?.sentBack || []} thresholds={th} showReasons onContext={setContextRow} />
                      </Group>
                      <Group title="Dropped" count={data.decisions?.dropped.length || 0}>
                        <Rows rows={data.decisions?.dropped || []} thresholds={th} showReasons onContext={setContextRow} />
                      </Group>
                      <Group title="Kept with a flag" count={data.decisions?.flagged.length || 0}>
                        <Rows rows={data.decisions?.flagged || []} thresholds={th} showReasons onContext={setContextRow} />
                      </Group>
                      <Group title="Kept" count={keptPlain.length} defaultOpen={false}>
                        <Rows rows={keptPlain} thresholds={th} showReasons={false} onContext={setContextRow} />
                      </Group>
                    </div>
                  )}

                  {tab === "speakers" && (
                    <div style={{ paddingTop: 8 }}>
                      {!data.speakers?.length && (
                        <Empty>
                          No speakers: this document has no transcript field.
                          {data.singleVoice ? ` One author — role ${data.singleVoice.role || "Unknown"} (${pct(data.singleVoice.roleConfidence)}), usage ${data.singleVoice.usage || "Unknown"} (cue ${pct(data.singleVoice.usageCue)}).` : ""}
                        </Empty>
                      )}
                      {!!data.speakers?.length && (
                        <Table>
                          <thead>
                            <tr><th>Speaker</th><th>Turns</th><th>Side</th><th>Role</th><th>Relationship</th><th>Usage</th></tr>
                          </thead>
                          <tbody>
                            {data.speakers.map((s) => {
                              const c = sideColors(s.side);
                              return (
                                <tr key={s.name}>
                                  <td style={{ color: colors2.black, fontWeight: 600 }}>{s.name}</td>
                                  <td className="num">{num(s.turns)}</td>
                                  <td><Pill $fg={c.fg} $bg={c.bg}>{s.side === "product" ? "product team" : s.side}</Pill> <Muted>{pct(s.productSideP)}</Muted></td>
                                  <td>{s.side === "product" ? <Muted>not tagged</Muted> : <>{s.role || <Muted>Unknown</Muted>} <Muted>{pct(s.roleConfidence)}</Muted></>}</td>
                                  <td>{s.relationship || <Muted>–</Muted>}</td>
                                  <td>{s.side === "product" ? <Muted>–</Muted> : <>{s.usage || "Unknown"} <Muted>cue {pct(s.usageCue)}</Muted></>}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                      <Small style={{ marginTop: 10 }}>
                        Side above 80% product-side is removed before moderation; roles need 50% to be tagged; usage needs its cue above 50%, else Unknown and no tag.
                      </Small>
                    </div>
                  )}

                  {tab === "fields" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
                      {(data.fields || []).map((f) => (
                        <Section key={f.valuePath}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 700, color: colors2.black }}>{f.label}</span>
                            <Pill $fg={BLUE.fg} $bg={BLUE.bg}>{words(f.nature || "unknown")}</Pill>
                            <Muted>{pct(f.confidence)}</Muted>
                            {f.hasSpeakerLabels != null && <Muted>· speaker labels {pct(f.hasSpeakerLabels)}</Muted>}
                          </div>
                          {!!f.units.length && (
                            <Table>
                              <thead><tr><th>Part</th><th>Chars</th><th>Triage</th><th>Respondent</th><th>Speaks</th><th>Junk</th></tr></thead>
                              <tbody>
                                {f.units.map((u, i) => (
                                  <tr key={i}>
                                    <td className="num">{u.chunkIdx == null ? "whole" : u.chunkIdx + 1}</td>
                                    <td className="num">{num(u.chars)}</td>
                                    <td>{u.skipped ? <Pill $fg={RED.fg} $bg={RED.bg}>skipped · {words(u.reasons.join(", "))}</Pill> : <Pill $fg={GREEN.fg} $bg={GREEN.bg}>annotated</Pill>}</td>
                                    <td className="num">{pct(u.scores?.respondent)}</td>
                                    <td className="num">{u.scores?.respondent_speaks != null ? pct(u.scores.respondent_speaks) : "–"}</td>
                                    <td className="num">{pct(u.scores?.gibberish)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </Section>
                      ))}
                    </div>
                  )}

                  {tab === "run" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
                      <Section>
                        <SectionHead as="div">Claude calls (highlights only)</SectionHead>
                        <Table>
                          <thead><tr><th>Field</th><th>Part</th><th>Nature</th><th>Highlights</th><th>Time</th></tr></thead>
                          <tbody>
                            {(data.c1 || []).map((c, i) => (
                              <tr key={i}>
                                <td>{c.field || <Muted>batched short fields</Muted>}</td>
                                <td className="num">{c.chunkIdx == null ? "–" : c.chunkIdx + 1}</td>
                                <td>{words(c.nature || "–")}</td>
                                <td className="num">{num(c.highlights)}</td>
                                <td className="num">{c.ms != null ? `${(c.ms / 1000).toFixed(1)}s` : "–"}</td>
                              </tr>
                            ))}
                            {!data.c1?.length && <tr><td colSpan={5}><Muted>No C1 records in this run log (older run, or okf-sub not forwarding the run id).</Muted></td></tr>}
                          </tbody>
                        </Table>
                      </Section>
                      <Section>
                        <SectionHead as="div">Tokens and cost</SectionHead>
                        <Table>
                          <thead><tr><th></th><th>Requests</th><th>Input (uncached)</th><th>Output</th><th>Cache read</th><th>Cache write</th><th>Cost</th></tr></thead>
                          <tbody>
                            <tr><td>Jev</td><td className="num">{num(data.usage?.jev.requests)}</td><td className="num">{num(data.usage?.jev.input)}</td><td className="num">{num(data.usage?.jev.output)}</td><td className="num">–</td><td className="num">–</td><td className="num">{usd(data.usage?.jev.costUsd)}</td></tr>
                            <tr><td>Claude C1</td><td className="num">{num(data.c1?.length)}</td><td className="num">{num(data.usage?.c1.input)}</td><td className="num">{num(data.usage?.c1.output)}</td><td className="num">{num(data.usage?.c1.cacheRead)}</td><td className="num">{num(data.usage?.c1.cacheWrite)}</td><td className="num">{usd(data.usage?.c1.costUsd)}</td></tr>
                            <tr><td style={{ fontWeight: 600 }}>Total</td><td /><td /><td /><td /><td /><td className="num" style={{ fontWeight: 600 }}>{usd(data.usage?.totalUsd)}</td></tr>
                          </tbody>
                        </Table>
                        <Small>
                          Token counts are the providers' own figures on each response, not estimates. Cost is list price × those counts:
                          {data.usage?.rates ? ` Jev $${data.usage.rates.jev.input}/M input (output free); ${data.usage.rates.claude.model || "Claude"} $${data.usage.rates.claude.input}/M input, $${data.usage.rates.claude.cacheRead}/M cache read, $${data.usage.rates.claude.cacheWrite}/M cache write, $${data.usage.rates.claude.output}/M output.` : ""}
                        </Small>
                      </Section>
                      <Section>
                        <SectionHead as="div">Thresholds in force</SectionHead>
                        <ThresholdLine th={th} />
                      </Section>
                    </div>
                  )}
                </Panel>
              </Body>
            </>
          )}
        </Wrap>
      {contextRow && <QuoteContextModal row={contextRow} field={fieldByPath.get(contextRow.valuePath)} onClose={() => setContextRow(null)} />}
    </Modal>
  );
};
